import type { FetchFunction } from './types';

/**
 * Which transport a call goes out on, and how the absence of one stays sayable.
 *
 * A leaf: it imports nothing from `request.ts`. The process-wide transport
 * arrives as an ARGUMENT rather than through an import, which is what keeps the
 * dependency running one way. Reading `request.fetch` from here would close the
 * loop `request.ts -> transport.ts -> request.ts`, and while a lazy read makes
 * that survive at runtime, an honest module graph is cheaper than a cycle that
 * happens to work.
 */

/**
 * The platform's own `fetch`, or `undefined` in a runtime that has none.
 *
 * A function rather than a plain read, and that is the whole point of it. The
 * DOM lib declares `globalThis.fetch` as always present, so a `const` annotated
 * `FetchFunction | undefined` is narrowed straight back to `FetchFunction` by control flow
 * and the absence becomes inexpressible without an assertion. MEASURED:
 * `oxlint --type-aware` rejects the comparison that follows such a const as
 * "types have no overlap". A RETURN type is not narrowed that way, so this is
 * what keeps the honest half of the union alive.
 *
 * The lib's claim is not true everywhere. MEASURED in a bare
 * `@jest-environment jsdom` realm: `Headers`, `FormData`, `Blob` and
 * `AbortController` are there, `fetch` is not.
 */
function platformFetch(): FetchFunction | undefined {
  return globalThis.fetch;
}

/**
 * The absence of a transport, as a value the compiler will not narrow away.
 *
 * `request.fetch` is declared `FetchFunction | undefined` and starts out unset. A
 * plain `undefined` on the right-hand side is narrowed to the literal type by
 * control flow, and the property then no longer accepts a transport being
 * assigned to it later -- which is the one thing it exists for. A return type
 * is not narrowed that way, the same reason `platformFetch` is a function
 * rather than a const.
 */
export function noTransport(): FetchFunction | undefined {
  return undefined;
}

/**
 * The transport a call goes out on: its own, then the process-wide one, then
 * the platform (FE18 to FE21).
 *
 * `undefined` means this runtime has no transport at all, which the caller
 * reports as `unsupported` rather than as a network failure (FE15).
 *
 * The process-wide transport is a parameter and not a read, so this module owes
 * nothing to the one that holds it.
 */
export function transportFor(
  own: FetchFunction | undefined,
  global: FetchFunction | undefined,
): FetchFunction | undefined {
  return own ?? global ?? platformFetch();
}
